import React from "react";
import { Box, Button, Chip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { formatRatingFilter } from "../../components/RatingFilterMenu.js";
import { formatReleaseYearFilter } from "../../components/ReleaseYearFilterMenu.js";
import {
  FILTER_CONFIG,
  getFilterOptions,
  getLanguageLabel,
} from "../../utils/utils.js";

// Same trailing clearance as SearchFilter's: the non-breaking space is what
// makes browsers count it towards the row's scrollable width
const TRAILING_SPACE_PX = 16;

/** "2025" → "2025", "2025-03" → "Mar 2025", "2025-03-14" → "14 Mar 2025" */
const formatPeriod = (token) => {
  const [year, month, day] = token.split("-").map(Number);
  if (!month) return String(year);
  return new Date(year, month - 1, day || 1).toLocaleDateString(undefined, {
    day: day ? "numeric" : undefined,
    month: "short",
    year: "numeric",
  });
};

const formatFinishDate = (value) => {
  if (value === "none") return "No finish date";
  if (!value.includes("..")) return formatPeriod(value);
  const [from, to] = value.split("..");
  if (from && to) return `${formatPeriod(from)} – ${formatPeriod(to)}`;
  return from ? `From ${formatPeriod(from)}` : `Until ${formatPeriod(to)}`;
};

/**
 * What a chip says: the filter's value rather than its name, e.g. "At least 3.5",
 * "Mar 2025", "Christopher Nolan", "Drama +2". Labels that depend on fetched
 * options (people, services) fall back to the filter's own name until loaded.
 */
const getChipLabel = (filter, { content, filterOptions, typeLabel }) => {
  const { type, value } = filter;

  if (type === "Rating") return formatRatingFilter(value);
  if (type === "Finished") return formatFinishDate(value);
  if (type === "Note") return value === "true" ? "With note" : "Without note";
  if (type === "Rewatch") {
    return { book: "Rereads", all: "Revisits" }[content] ?? "Rewatches";
  }
  if (type === "Year") return formatReleaseYearFilter(value);

  const valueLabels = {
    Language: (v) => getLanguageLabel(v),
    Person: (v) => filterOptions.Person?.find((p) => p.id === v)?.name,
    Provider: (v) => filterOptions.Provider?.find((p) => p.id === v)?.name,
  };
  const first = valueLabels[type] ? valueLabels[type](value[0]) : value[0];
  if (!first) {
    return value.length > 1 ? `${typeLabel} · ${value.length}` : typeLabel;
  }
  return value.length > 1 ? `${first} +${value.length - 1}` : first;
};

/**
 * The Track header's second row: one chip per active filter, summarising its
 * value, led by the applied title search if there is one. Tapping a chip edits
 * it, its ✕ removes it, and "Clear filter(s)" removes them all — pushed to
 * the row's right edge on a phone. Scrolls sideways when it overflows.
 */
export default function ActiveFilterChips({
  filters,
  status,
  content,
  filterOptions,
  onChipClick,
  onRemove,
  searchTerm,
  onSearchChipClick,
  onSearchRemove,
  onClearAll,
}) {
  const menuOptions = getFilterOptions(status, content);
  const chipCount = filters.length + (searchTerm ? 1 : 0);

  return (
    // Spaced with gap rather than Stack's child margins, which would override
    // the clear button's auto margin
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {searchTerm && (
        <Chip
          icon={<SearchIcon />}
          label={`"${searchTerm}"`}
          variant="outlined"
          size="small"
          onClick={onSearchChipClick}
          onDelete={onSearchRemove}
          sx={{
            flexShrink: 0,
            maxWidth: 220,
          }}
        />
      )}

      {/* FILTER_CONFIG order, so chips don't shuffle as filters are edited */}
      {FILTER_CONFIG.map((config) => ({
        config,
        filter: filters.find((f) => f.type === config.type),
      }))
        .filter(({ filter }) => filter)
        .map(({ config, filter }) => {
          const option = menuOptions.find((o) => o.value === filter.type);
          return (
            <Chip
              key={filter.type}
              icon={option.icon}
              label={getChipLabel(filter, {
                content,
                filterOptions,
                typeLabel: option.label,
              })}
              variant="outlined"
              size="small"
              // A toggle has nothing to edit, only ✕ to remove
              onClick={
                config.toggle
                  ? undefined
                  : (event) => onChipClick(filter.type, event)
              }
              onDelete={() => onRemove(filter.type)}
              sx={{
                flexShrink: 0,
                maxWidth: 220,
              }}
            />
          );
        })}

      <Button
        size="small"
        onClick={onClearAll}
        sx={{
          flexShrink: 0,
          whiteSpace: "nowrap",
          // No vertical padding, so the button is no taller than the chips
          py: 0,
          // To the right edge on a phone, while the chips leave room
          ml: { xs: "auto", sm: 0 },
        }}
      >
        {chipCount > 1 ? "Clear filters" : "Clear filter"}
      </Button>

      <Box
        aria-hidden
        sx={{
          width: TRAILING_SPACE_PX,
          flexShrink: 0,
          // On a phone, takes no room in the row (cancelling its own width
          // and the gap before it), so the clear button sits flush with the
          // edge — while still adding its clearance once the row overflows
          mr: { xs: `-${TRAILING_SPACE_PX + 8}px`, sm: 0 },
        }}
      >
        {" "}
      </Box>
    </Box>
  );
}
